"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.searchGrnsQuerySchema = exports.editPurchaseReturnReasonSchema = exports.recentPurchaseReturnsQuerySchema = exports.returnGrnLineSchema = exports.grnNumberQuerySchema = exports.lastPurchasePriceQuerySchema = exports.checkDuplicateGrnQuerySchema = exports.postVendorBillSchema = exports.updateGoodsReceiptNoteSchema = exports.createGoodsReceiptNoteSchema = exports.goodsReceiptNoteShape = exports.grnLineSchema = exports.grnLineShape = void 0;
const zod_1 = require("zod");
exports.grnLineShape = {
    // Nullable — a Direct GRN line (no Purchase Order) has none. See
    // `goods-receipt-note.service.ts`'s branch on `purchaseOrderId`.
    poLineId: zod_1.z.string().uuid().optional(),
    drugId: zod_1.z.string().uuid().optional(),
    itemId: zod_1.z.string().uuid().optional(),
    batchNumber: zod_1.z.string().min(1).max(50),
    expiryDate: zod_1.z.coerce.date().optional(),
    manufacturingDate: zod_1.z.coerce.date().optional(),
    receivedQuantityPurchaseUnit: zod_1.z.number().positive(),
    // Extra units received for FREE (e.g. a "100+10 free" vendor scheme) —
    // see `GRNLine.freeQuantityPurchaseUnit`'s schema doc comment. Added to
    // real stock quantity, but never to Basic Value/cost.
    freeQuantityPurchaseUnit: zod_1.z.number().nonnegative().optional(),
    // Whether GST still applies to the free quantity's notional value —
    // see `GRNLine.gstOnFreeQty`'s schema doc comment.
    gstOnFreeQty: zod_1.z.boolean().optional(),
    // Per-line override of `DrugMaster.conversionFactor` — see
    // `GRNLine.unitsPerPurchaseUnit`'s schema doc comment.
    unitsPerPurchaseUnit: zod_1.z.number().positive().optional(),
    unitCostPurchaseUnit: zod_1.z.number().int().nonnegative(),
    // Reference-only, captured from the physical pack at receipt time — see
    // `GRNLine.mrp`'s schema doc comment; not wired into `PriceListLine.mrp`.
    mrp: zod_1.z.number().int().nonnegative().optional(),
    // Informational only — `unitCostPurchaseUnit` is submitted GROSS
    // (discount is NEVER applied to it); see `GRNLine.discountPercent`'s
    // schema doc comment.
    discountPercent: zod_1.z.number().min(0).max(100).optional(),
    // The GST rate actually selected in this line's own GST% dropdown —
    // see `GRNLine.gstPercent`'s schema doc comment for why this is now
    // persisted directly instead of only living inside `taxAmount`.
    gstPercent: zod_1.z.number().min(0).max(100).optional(),
    // "Allow discount" (owner, 2026-10-30) — may the batch this line becomes be discounted when it is sold? See `GRNLine.discountAllowed`. Default false.
    discountAllowed: zod_1.z.boolean().optional(),
    taxAmount: zod_1.z.number().int().nonnegative().optional(),
    // Free-text — the physical batch's actual manufacturer as printed on the
    // pack, entered per receipt. Deliberately NOT tied to `DrugMaster`'s own
    // manufacturer master (a generic drug is routinely sourced from a
    // different manufacturer batch-to-batch, and the operator needs to
    // record whichever one this specific delivery actually came from).
    manufacturer: zod_1.z.string().max(150).optional(),
    // Real feedback (2026-07-30) — decision 7's strict over-receipt block
    // stays the default; providing this is the explicit, audited opt-in
    // that lets a line exceed its PO line's `orderedQuantity` (e.g. the
    // vendor physically shipped more than ordered). See
    // `GRNLine.overReceiveReason`'s schema doc comment.
    overReceiveReason: zod_1.z.string().min(1).max(500).optional(),
    // Owner, 2026-10-30 (rack system): the rack master record this line's batch will be shelved on. Optional on purpose — a line left
    // without one still posts and its batch is simply "unracked" (receiving is never blocked by a missing rack). Validated server-side:
    // it must exist, be active, and stand in the GRN's receiving location.
    rackId: zod_1.z.string().uuid().optional(),
};
// A drug line with no real MRP captured creates a batch that LOOKS
// received (real stock, real cost) but can never actually be dispensed
// — `MedicineEntryGrid.tsx`'s own "no MRP captured on its Pharmacy
// Counter batch" block refuses to sell it, and nothing catches that
// until a pharmacist hits it live at the counter, sometimes weeks later.
// `mrp` stays genuinely optional for an `itemId` line (MRP isn't a real
// concept for non-drug supply items), which is why this is a targeted
// `superRefine`, not just tightening `mrp` itself to non-optional.
//
// Real feedback — `gstPercent` gets the exact same treatment, added
// alongside the pre-existing `mrp` check: a drug batch received with no
// GST% captured bills at 0% GST silently (never refused — GST isn't
// price, so nothing downstream blocks it the way a missing MRP does),
// which is a real tax-compliance gap, not just a UX one. `0` itself is a
// genuinely valid GST slab (some drugs/consumables ARE zero-rated), so
// this checks for `undefined` specifically, never `<= 0`.
exports.grnLineSchema = zod_1.z.object(exports.grnLineShape).superRefine((line, ctx) => {
    if (line.drugId && (line.mrp === undefined || line.mrp <= 0)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['mrp'],
            message: 'MRP is required for a drug line — without it, this batch can be received but never actually dispensed',
        });
    }
    if (line.drugId && line.gstPercent === undefined) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['gstPercent'],
            message: 'GST % is required for a drug line — without it, this batch would silently bill at 0% GST',
        });
    }
});
exports.goodsReceiptNoteShape = {
    branchId: zod_1.z.string().uuid(),
    hospitalId: zod_1.z.string().uuid().optional(),
    // Optional — omitted for a Direct GRN (goods received without ever
    // raising a Purchase Order). `vendorId` is then required directly
    // (see the `.refine()` below); for a PO-sourced GRN, `vendorId` is
    // resolved server-side from the PO and any caller-supplied value is
    // ignored.
    purchaseOrderId: zod_1.z.string().uuid().optional(),
    vendorId: zod_1.z.string().uuid().optional(),
    receivingLocationId: zod_1.z.string().uuid(),
    vendorBillNumber: zod_1.z.string().min(1).max(100),
    // The date printed on the vendor's own invoice/bill — distinct from
    // `receiptDate` (when the goods were physically received), which can
    // genuinely differ. Optional: not every informal/emergency purchase has
    // a dated bill in hand yet.
    vendorBillDate: zod_1.z.coerce.date().optional(),
    receiptDate: zod_1.z.coerce.date(),
    remarks: zod_1.z.string().optional(),
    // When true, `createGrn()` stops after saving the DRAFT header+lines —
    // no immediate stock posting. Omitted/false keeps today's behavior
    // (create-and-receive in one step). A saved DRAFT can later be edited
    // (`PATCH /:id`) and explicitly confirmed (`POST /:id/post`).
    saveAsDraft: zod_1.z.boolean().optional(),
};
exports.createGoodsReceiptNoteSchema = zod_1.z
    .object({
    ...exports.goodsReceiptNoteShape,
    lines: zod_1.z.array(exports.grnLineSchema).min(1),
})
    .refine((input) => Boolean(input.purchaseOrderId) || Boolean(input.vendorId), {
    message: 'vendorId is required for a Direct GRN (no purchaseOrderId)',
    path: ['vendorId'],
});
// Editing is DRAFT-only (enforced service-side) and always re-saves the
// full header+lines — same shape as create, since an edit is really
// "replace this draft's content," not a partial patch.
exports.updateGoodsReceiptNoteSchema = exports.createGoodsReceiptNoteSchema;
exports.postVendorBillSchema = zod_1.z.object({
    vendorId: zod_1.z.string().uuid(),
    vendorBillNumber: zod_1.z.string().min(1).max(100),
    grnIds: zod_1.z.array(zod_1.z.string().uuid()).min(1),
});
exports.checkDuplicateGrnQuerySchema = zod_1.z.object({
    vendorId: zod_1.z.string().uuid(),
    vendorBillNumber: zod_1.z.string().min(1).max(100),
    excludeId: zod_1.z.string().uuid().optional(),
});
/** Merge & Create Draft POs' per-line, per-vendor unit-price prefill — exactly one of drugId/itemId. */
exports.lastPurchasePriceQuerySchema = zod_1.z
    .object({
    drugId: zod_1.z.string().uuid().optional(),
    itemId: zod_1.z.string().uuid().optional(),
    vendorId: zod_1.z.string().uuid(),
})
    .refine((data) => Boolean(data.drugId) !== Boolean(data.itemId), {
    message: 'Provide exactly one of drugId or itemId',
});
// Real feedback — Purchase Return workstation's "Original GRN" / scan
// step, GET /goods-receipt-notes/lookup?grnNumber=. Exact match, same
// posture as Sales Return's own `saleNumberQuerySchema` / Ward Return's
// `movementNumberQuerySchema`.
exports.grnNumberQuerySchema = zod_1.z.object({
    grnNumber: zod_1.z.string().trim().min(1),
});
// POST /goods-receipt-notes/lines/:lineId/return body. `quantity`
// omitted = return everything still returnable on this line (see
// `PurchaseReturnService.returnGrnLine()`), same posture as Ward
// Return's own `returnMovementLineSchema`.
exports.returnGrnLineSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().min(1).max(255),
    quantity: zod_1.z.number().positive().optional(),
});
// Real feedback — Purchase Return's own Return History tab,
// GET /goods-receipt-notes/returns/recent. Mirrors Sales Return's own
// `recentReturnsQuerySchema` (`saleNumber` → `grnNumber`).
exports.recentPurchaseReturnsQuerySchema = zod_1.z.object({
    limit: zod_1.z.coerce.number().int().positive().max(200).optional(),
    returnNumber: zod_1.z.string().trim().min(1).optional(),
    grnNumber: zod_1.z.string().trim().min(1).optional(),
    fromDate: zod_1.z.coerce.date().optional(),
    toDate: zod_1.z.coerce.date().optional(),
});
// Real feedback — Purchase Return History's "Edit Reason" action. Same
// narrow, audited exception as Sales/Ward Return History's own
// `editReturnReasonSchema` — only the reason text is ever editable.
exports.editPurchaseReturnReasonSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().min(1).max(255),
});
// Real feedback — Purchase Return workstation's "Original GRN"
// typeahead, GET /goods-receipt-notes/search?q=. Mirrors Sales Return's
// own `searchSalesQuerySchema`.
exports.searchGrnsQuerySchema = zod_1.z.object({
    q: zod_1.z.string().trim().min(1),
    limit: zod_1.z.coerce.number().int().positive().max(25).optional(),
});
//# sourceMappingURL=goods-receipt-note.schema.js.map